import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate, formatIST, formatDueIn } from "../../utils/formatDate";
import { getAllPurposes } from "../../services/purposes";
import { useIsMobile } from "../../hooks/useMediaQuery";
import { Badge } from "./Badge";
import { SubmissionStatusBadge, SubmissionTypeBadge } from "./SubmissionBadges";
import { Button } from "./Button";

const EMAIL_RE = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;

const normalizeKey = (str) =>
  str
    ?.toString()
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "") ?? "";

// My Data's attribute names ("Email") and a purpose's dataCategories ("Email
// Address") refer to the same category but aren't identical strings, so an
// exact normalizeKey match misses real matches — fall back to substring
// containment either direction.
const categoriesMatch = (a, b) => {
  if (!a || !b) return false;
  return a === b || a.includes(b) || b.includes(a);
};

function Linkify({ text }) {
  if (!text) return null;
  const parts = text.split(EMAIL_RE);
  return parts.map((part, i) =>
    EMAIL_RE.test(part) ? (
      <a
        key={i}
        href={`mailto:${part}`}
        className="text-blue-600 hover:underline"
      >
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}

function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className="text-sm font-medium text-gray-800">{value || "-"}</div>
    </div>
  );
}

export default function RequestDetailsPanel({ request, tenantId, onClose }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const r = request || {};
  const submissionType = (r.submission_type || "").toLowerCase();
  const isComplaint = submissionType === "complaint";
  const isErasure = submissionType === "erasure";
  const dataFields = r.submission_data_filed || [];
  const dueIn = formatDueIn(r.due_in);
  const isResolved = (r.status || "").toLowerCase() === "resolved";

  // Requests aren't scoped to a purpose at submission time, so the affected
  // purpose(s) are derived here by matching the request's data categories
  // against each tenant purpose's dataCategories (same approach as the
  // Submit Request modal's "Affected Purposes" preview).
  const [purposes, setPurposes] = useState([]);
  useEffect(() => {
    if (!tenantId) return;
    getAllPurposes(tenantId, { page: 1, page_size: 100 })
      .then((res) => setPurposes(res.data?.data?.items || []))
      .catch(() => setPurposes([]));
  }, [tenantId]);

  const categoryKeys = dataFields
    .map((f) => normalizeKey(f.field_name))
    .filter(Boolean);
  const affectedPurposeNames = purposes
    .filter((p) =>
      (p.dataCategories ?? p.data_categories ?? []).some((c) => {
        const key = normalizeKey(c);
        return categoryKeys.some((catKey) => categoriesMatch(key, catKey));
      }),
    )
    .map((p) => p.purpose ?? p.name);

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 md:bg-black/50"
      onClick={!isMobile ? onClose : undefined}
    >
      <div
        className="absolute inset-0 md:inset-auto md:right-0 md:top-0 md:h-full w-full md:max-w-3xl bg-white shadow-xl overflow-y-auto animate-slide-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-100 md:border-gray-100 bg-gray-50 md:bg-white sticky top-0 md:static">
          {isMobile ? (
            <Button
              variant="tertiary"
              onClick={onClose}
              className="!text-gray-700 !p-1 -ml-1"
              aria-label="Back"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </Button>
          ) : (
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold text-gray-800">
                {r.request_id || r.id}
              </h2>
            </div>
          )}
          {isMobile && (
            <span className="text-base font-semibold text-gray-800">
              {r.request_id || r.id}
            </span>
          )}
          <Button
            variant="tertiary"
            onClick={isMobile ? () => navigate("/rights") : onClose}
            className={
              isMobile
                ? "!text-gray-700 !p-1 -mr-1"
                : "!text-gray-400 hover:!text-gray-600"
            }
            aria-label={isMobile ? "Help" : "Close"}
          >
            {isMobile ? (
              <img src="/assets/rights.svg" alt="" width={22} height={22} />
            ) : (
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            )}
          </Button>
        </div>

        <div className="p-4 md:p-6 space-y-6">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              Overview
            </h3>
            <div className="md:border border-gray-100 bg-[#F9F8FF] rounded-lg p-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Field label="Request ID" value={r.request_id || r.id} />
                <div>
                  <div className="text-xs text-gray-500 mb-1">Type</div>
                  <SubmissionTypeBadge type={r.submission_type} />
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Status</div>
                  <SubmissionStatusBadge status={r.status} />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Field
                  label="Raised On"
                  value={r.created_at && formatDate(r.created_at)}
                />
                {isResolved ? (
                  <Field
                    label="Resolved On"
                    value={r.updated_at && formatDate(r.updated_at)}
                  />
                ) : (
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Due</div>
                    <div className="text-sm font-semibold text-[#C73838]">
                      {dueIn || "-"}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">
                  Affected Purposes
                </div>
                {affectedPurposeNames.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {affectedPurposeNames.map((name, i) => (
                      <Badge key={i} value={name} variant="purpose" size="sm" />
                    ))}
                  </div>
                ) : (
                  <div className="text-sm font-medium text-gray-800">-</div>
                )}
              </div>
            </div>
          </div>

          {(dataFields.length > 0 || r.description) && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                {isComplaint
                  ? "Complaint Information"
                  : isMobile
                    ? "Requested Changes"
                    : "Request Information"}
              </h3>
              <div className="bg-[#F9F8FF] rounded-lg p-4 space-y-4">
                {dataFields.map((f, idx) => (
                  <div
                    key={idx}
                    className="pb-4 last:border-0 last:pb-0"
                  >
                    <div className="md:hidden">
                      <div className="text-xs text-gray-400 mb-1">
                        {f.field_name}
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-gray-800">
                          {f.old_value || "-"}
                        </span>
                        {!isErasure && (
                          <>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="shrink-0 text-gray-400"
                            >
                              <line x1="5" y1="12" x2="19" y2="12" />
                              <polyline points="12 5 19 12 12 19" />
                            </svg>
                            <span className="text-sm font-semibold text-gray-800">
                              {f.new_value || "-"}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div
                      className={`hidden md:grid ${isErasure ? "grid-cols-2" : "grid-cols-3"} gap-4`}
                    >
                      <Field
                        label="Data Category"
                        value={f.field_name}
                      />
                      <Field
                        label={isErasure ? "Value" : "Current Value"}
                        value={f.old_value}
                      />
                      {!isErasure && (
                        <Field label="Updated Value" value={f.new_value} />
                      )}
                    </div>
                  </div>
                ))}

                {r.description && (
                  <div
                   
                  >
                    <h4 className="text-xs text-gray-500 mb-1">
                      {isComplaint ? "Complaint Message" : "Request Message"}
                    </h4>
                    <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">
                      <Linkify text={r.description} />
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
